import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { ArrowRightSVG } from '@svgComponents/arrowRightSVG';
import { CircleSVG } from '@svgComponents/CircleSVG';
import { FailedSVG } from '@svgComponents/failedSVG/index';
import { TickCircleSVG } from '@svgComponents/tickCircleSVG';
import classnames from 'classnames';

import styles from '../documentRequirements/DocumentRequirements.module.css';
import LatestBankStatements from '../latestBankStatements/LatestBankStatements';

export const DocumentRequirementsDE = ({
    documentRequirements,
    uploadDocumentRequirement,
    hasSubmitFailed
}: {
    documentRequirements: any;
    uploadDocumentRequirement: (documentType: {
        label: string;
        value: string;
    }) => void;
    hasSubmitFailed: boolean;
}) => {
    const { branch } = useSplitTestBranch({
        test: 'de_6m_pdf_bs_test_2026_q2',
        suspense: false
    });
    const numberOfBankStatementsInMonths = branch === 'INCLUDE' ? 6 : 3;

    return (
        <>
            {documentRequirements.map(
                ({
                    status,
                    type,
                    hasItems,
                    hasError,
                    uploadModalDocType
                }: any) => {
                    if (
                        type === 'kontoauszuege_hochladen' //bank_statement
                    ) {
                        return (
                            <LatestBankStatements
                                key={type}
                                hasSubmitFailed={hasSubmitFailed}
                                numberOfBankStatementsInMonths={
                                    numberOfBankStatementsInMonths
                                }
                                statusIndicator={
                                    <DocumentStatusIndicator
                                        status={status}
                                        hasItems={hasItems}
                                        hasError={hasError}
                                    />
                                }
                            />
                        );
                    }

                    return (
                        <div
                            className={styles.documentRequirement}
                            onClick={() =>
                                uploadDocumentRequirement(uploadModalDocType)
                            }
                            data-testid={`${type}-requirement`}
                            key={type}
                            role="button"
                        >
                            <DocumentStatusIndicator
                                status={status}
                                hasItems={hasItems}
                                hasError={hasError}
                            />

                            <div className={styles.contentWrapper}>
                                <span className={styles.documentLabel}>
                                    <DocumentRequirementLabel type={type} />
                                </span>
                            </div>

                            <ArrowRightSVG className={styles.documentArrow} />

                            {status !== 'pending' &&
                                status !== 'completed' &&
                                hasSubmitFailed && (
                                    <div
                                        data-testid="formErrors"
                                        className={styles.error}
                                    >
                                        <span>
                                            Bitte laden Sie Ihre Unterlagen hoch
                                        </span>
                                    </div>
                                )}
                        </div>
                    );
                }
            )}
        </>
    );
};

const DocumentRequirementLabel = ({ type }: { type: string }) => {
    const { branch } = useSplitTestBranch({
        test: 'de_6m_pdf_bs_test_2026_q2',
        suspense: false
    });
    const bankStatementDays = branch === 'INCLUDE' ? 180 : 90;

    switch (type) {
        case 'kontoauszuege_hochladen':
            return (
                <>
                    Kontoauszüge{' '}
                    <span className={styles.documentLabelCaption}>
                        der letzten {bankStatementDays} Tage im pdf-Format
                    </span>
                </>
            );
        case 'bwa_hochladen':
            return (
                <>
                    Betriebswirtschaftliche Auswertung (oder Gewinn und
                    Verlustrechnung / Einnahmenüberschussrechnung)
                </>
            );
        case 'jaehrliche_bwa_susa_hochladen':
            return (
                <>
                    Betriebswirtschaftliche Auswertung inkl. Summen- und
                    Saldenliste (oder Jahresabschluss){' '}
                    <span className={styles.documentLabelCaption}>
                        der letzten beiden Geschäftsjahre
                    </span>
                </>
            );
        case 'bwa_susa_hochladen':
            return (
                <>
                    Aktuelle betriebswirtschaftliche Auswertung inkl. Summen-und
                    Saldenliste{' '}
                    <span className={styles.documentLabelCaption}>
                        nicht älter als 4 Monate
                    </span>
                </>
            );
        default:
            return null;
    }
};

const DocumentStatusIndicator = ({
    status,
    hasItems,
    hasError
}: {
    status: string;
    hasItems: boolean;
    hasError: boolean;
}) => {
    if (hasError)
        return (
            <FailedSVG
                className={classnames(styles.statusIcon, styles.failedIcon)}
            />
        );
    if (hasItems || status === 'completed')
        return <TickCircleSVG className={styles.statusIcon} />;

    return <CircleSVG className={styles.statusIcon} />;
};
